<template>
  <div class="submit-tender">
    <button
      id="submit_tender"
      type="button"
      :class="className"
      :disabled="disabled"
      @click="init()"
    >
      {{ $t('action.submit') }}
    </button>
    <modal
      ref="chooseApprModal"
      modalId="choose-appr-modal"
    >
      <div slot="modal-header">
        <p class="modal-pure-text">
          {{ $t('handover.submit.chooseApprover') }}
        </p>
      </div>
      <div slot="content">
        <HKLSelect
          class="submit-hkl-select"
          :value="approverId || undefined"
          :options="approverHklOptions"
          :showSearch="true"
          placeholder=""
          @change="onApproverChange"
        />
        <span
          v-if="apprErrHint"
          class="form-error is-visible appr-error-message"
        >
          {{ apprErrHint }}
        </span>
      </div>
      <div slot="btns">
        <button
          type="button"
          class="btn btn--darkblue"
          aria-label="Close reveal"
          @click="closeApprModal()"
        >
          {{ $t('action.cancel') }}
        </button>
        <button
          type="submit"
          class="btn btn--orange"
          @click="submitHandover()"
        >
          {{ $t('action.submit') }}
        </button>
      </div>
    </modal>

    <modal
      ref="alertModal"
      modalId="alert-modal"
    >
      <div slot="content">
        <p class="modal-pure-text">
          {{ isHandover ? $t('handover.submit.submitted') : $t('takeback.submit.submitted') }}
        </p>
      </div>
      <div slot="btns">
        <button
          type="button"
          class="btn btn--orange"
          @click="navTo()"
        >
          {{ $t('hint.okay') }}
        </button>
      </div>
    </modal>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from 'vuex'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import mutationTypes from '@/store/mutation-types'
import { sendEmail } from '@/api/postman/request'
import { utils as formUtils } from '@/plugin/InputChange'
import { aclHelper, tmsPermsTypes } from '@/utils/aclHelper'
import { EmailTypes, ActionTypes } from '@/utils/consts'
import EventBus from '@/utils/eventBus'
import { hotbStatus, hotbAttrTypes } from '@/utils/locale'
import navigation from '@/utils/navigation'
import Modal from '@/controls/Modal'
import { updateCompanyNameAndTradeNameIfEmpty } from './utils'

export default {
  name: 'SubmitHandover',
  components: {
    HKLSelect,
    Modal,
  },
  props: {
    className: {
      type: String,
      default: 'btn btn--orange',
    },
    id: {
      type: String,
      default: 'handoverSubmitPopup',
    },
    options: {
      type: Array,
      default: [],
    },
    resetValidation: {
      type: Function,
      default: null,
    },
    validate: {
      type: Function,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: true,
    },
  },

  data () {
    return {
      apprErrHint: '',
      approverId: null,
    }
  },

  beforeDestroyed () {
    this.alertModal.destroy()
    this.apprModal.destroy()
  },
  computed: {
    ...mapGetters('hotb', ['currentHotb', 'isHandover']),
    ...mapGetters('user', ['currentUser']),
    approverHklOptions () {
      return (this.options || []).map(opt => ({
        name: opt.name,
        value: opt.id,
      }))
    },
  },
  methods: {
    ...mapActions('hotb', ['loadHotb', 'updateHotb', 'patchStatus']),
    ...mapMutations('hotb', {
      updateFieldByPath: mutationTypes.HOTB_UPDATE_FIELD_PATH,
    }),
    async init () {
      this.resetValidation && this.resetValidation()
      const isValidate = await this.validate()
      if (this.validate && !isValidate) return

      if (aclHelper.getAccess(tmsPermsTypes.hotb.approve)) {
        this.updateFieldByPath({ path: ['approvedBy'], newValue: this.currentUser.id })
        updateCompanyNameAndTradeNameIfEmpty()
        await this.updateHotb({ notify: false })
        this.$emit('hotb:approve')
        return
      }

      this.alertModal = this.$refs.alertModal
      this.apprModal = this.$refs.chooseApprModal
      this.openApprModal()
    },
    openApprModal () {
      this.apprModal.open()
    },
    openAlertModal () {
      this.alertModal.open()
    },
    closeAlertModal () {
      this.alertModal.close()
    },
    closeApprModal () {
      this.apprModal.close()
    },
    onApproverChange (id) {
      this.approverId = id || null
      if (this.apprErrHint) {
        this.apprErrHint = ''
      }
    },
    isValidApprover () {
      return this.approverId && (this.options.some(option => option.id === this.approverId))
    },
    async submitHandover () {
      if (!this.isValidApprover()) {
        this.apprErrHint = this.$i18n.t('tender.submit.apprRequired')
        return
      }
      this.updateFieldByPath({ path: ['approvedBy'], newValue: this.approverId })
      const attrToUpdate = this.currentHotb.status === hotbStatus.APPR ? {attrs: ['transactionDate', 'approvedBy']} : {}
      await this.updateHotb(attrToUpdate)
      const hotbType = hotbAttrTypes[this.currentHotb.type]
      await this.updateStatus(hotbStatus.WAPPR, '', ActionTypes.hotb.submit)
      sendEmail(EmailTypes[hotbType].submitForApproval)
      formUtils.resetDirty()
      this.closeApprModal()
      this.openAlertModal()
    },
    async updateStatus (status, comments, action) {
      await this.patchStatus({ request: { status, comments }, action })
      await this.loadHotb(this.currentHotb.id)
      formUtils.resetDirty()
    },

    navTo (name) {
      this.closeAlertModal()
      EventBus.$emit('modules-handovers-submit-disable-edit')
      navigation.toPage(name)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/popup';
@import "~styles/stickyBtn";
.appr-error-message {
  margin-top: 10px;
}
.submit-tender {
  button {
    display: block;
  }
}
#choose-appr-modal {
  .form-error {
    margin-bottom: 0;
    margin-top: 3px;
  }
}

</style>
